import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { Grid } from '@mui/material'
import BarChart from './BarChart'
import { HeaderTypography, WelcomeTypography } from './styles'
import { useAuth, useUser } from '../../services/contexts'
import { bffService } from '../../services/utils/axios'

export const Periods = {
  YESTERDAY: 'YESTERDAY',
  YEAR: 'YEAR',
  TIMEPERIOD: 'TIMEPERIOD',
}

/**
 * Generate date range based on the selected period
 * @param {string} period - The selected period (YESTERDAY, YEAR, TIMEPERIOD)
 * @returns {Object} Object containing startDate and endDate
 */
const getDateRange = (period) => {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const getStartOfDay = (date) => {
    const result = new Date(date)
    result.setHours(0, 0, 0, 0)
    return result
  }
  const getEndOfDay = (date) => {
    const result = new Date(date)
    result.setHours(23, 59, 59, 999)
    return result
  }

  switch (period) {
    case Periods.YESTERDAY:
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)
      return {
        startDate: getStartOfDay(yesterday),
        endDate: getEndOfDay(yesterday),
      }

    case Periods.YEAR:
      const startOfYear = new Date(today.getFullYear(), 0, 1)
      const endOfYear = new Date(today.getFullYear(), 11, 31)
      return {
        startDate: getStartOfDay(startOfYear),
        endDate: getEndOfDay(endOfYear),
      }

    case Periods.TIMEPERIOD:
      // For TIMEPERIOD, you might want to implement a custom date picker
      // For now, defaulting to current year
      const startOfCurrentYear = new Date(today.getFullYear(), 0, 1)
      const endOfCurrentYear = new Date(today.getFullYear(), 11, 31)
      return {
        startDate: getStartOfDay(startOfCurrentYear),
        endDate: getEndOfDay(endOfCurrentYear),
      }

    default:
      return {
        startDate: getStartOfDay(new Date(today.getFullYear(), 0, 1)),
        endDate: getEndOfDay(new Date(today.getFullYear(), 11, 31)),
      }
  }
}

/**
 * This is the entry point for dashboard front page.
 * It holds all the KPIs and grpah.
 *
 * @returns {React.ReactNode} A React component that renders all the KPIs.
 */
function Default() {
  const { t } = useTranslation()
  const { token } = useAuth()
  const { operator } = useUser()
  const [period, setPeriod] = useState(Periods.YEAR)
  const [energyConsumptionData, setEnergyConsumptionData] = useState([])
  // State variables for custom date selection
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [usersCount, setUsersCount] = useState(null)
  const [loading, setLoading] = useState(true)
  const [contractStats, setContractStats] = useState(null)

  // Update date range when period changes
  useEffect(() => {
    const dateRange = getDateRange(period)
    setStartDate(dateRange.startDate)
    setEndDate(dateRange.endDate)
  }, [period])

  useEffect(() => {
    // Only make API call when we have valid dates
    if (startDate && endDate && token) {
      bffService
        .get('/energy-values-by-month', {
          params: {
            start_date: new Date(
              new Date(startDate).setHours(0, 0, 0, 0),
            ).toISOString(),
            end_date: new Date(
              new Date(endDate).setHours(23, 59, 59, 999),
            ).toISOString(),
          },
        })
        .then((response) => {
          setEnergyConsumptionData(response.data)
          setLoading(false)
        })
        .catch((error) => {
          setLoading(false)
          console.log(error)
        })
    }
  }, [token, startDate, endDate])

  useEffect(() => {
    if (token) {
      bffService.get('/company/contract-stats').then((response) => {
        setContractStats(response.data)
      })
      bffService.get('/users/count').then((response) => {
        setUsersCount(response?.data?.count)
      })
    }
  }, [token])

  return (
    <React.Fragment>
      <Helmet title="Default Dashboard" />
      <Grid container spacing={1}>
        {/* Welcome message and page title */}
        <Grid item xs={12} lg={12}>
          <WelcomeTypography variant="h6" gutterBottom display="inline">
            {t('LBLCockpitTitle')},{' '}
            {operator?.name === null ? '' : operator?.name}
          </WelcomeTypography>
        </Grid>
        <Grid item xs={12} lg={12}>
          <HeaderTypography variant="h2" gutterBottom display="inline">
            {t('LBLCockpit')}
          </HeaderTypography>
        </Grid>
        <Grid item xs={12} lg={12}>
          <BarChart
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            startDate={startDate}
            endDate={endDate}
            period={period}
            setPeriod={setPeriod}
            data={energyConsumptionData}
            loading={loading}
            contractStats={contractStats}
            usersCount={usersCount}
          />
        </Grid>
      </Grid>
    </React.Fragment>
  )
}
export default Default
